import { useCallback, useState } from "react";

import {
  Avatar,
  Button,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuProps,
  MenuTriggerChildProps,
  Text,
  Tooltip,
} from "@fluentui/react-components";
import { Menu, MenuTrigger } from "@fluentui/react-components";
import { ChevronUpDownFilled } from "@fluentui/react-icons";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { ROUTES } from "library/routes";
import { EProfileTab } from "library/types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { chatIdAtom, selectedClientAtom } from "store";
import { selectedProfileTabAtom } from "store/UIProfilePage";
import styled from "styled-components";

import { DocumentsDialog } from "components/DocumentsDialog/DocumentsDialog";
import { Flex } from "components/styled";
import { useAppTheme } from "hooks/useAppTheme";
import { useHasGivenConsent } from "hooks/useHasGivenConsent";
import { useGetUserClients } from "state/useGetUserClients";

type Props = {
  togglePanel?: () => void;
};

export function SelectClient({ togglePanel }: Props) {
  const { t } = useTranslation();
  const theme = useAppTheme();
  const { data: clientOptions = [] } = useGetUserClients();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [selectedClient, setSelectedClient] = useAtom(selectedClientAtom);

  const [showDocuments, setShowDocuments] = useState(false);

  const chatSessionId = useAtomValue(chatIdAtom);

  const setSelectedProfileTab = useSetAtom(selectedProfileTabAtom);

  const hasGivenConsent = useHasGivenConsent();

  const onOpenChange: MenuProps["onOpenChange"] = (e, data) => {
    setOpen(data.open);
  };

  const setDocumentOpenState = useCallback((state) => {
    setShowDocuments(state);
  }, []);

  const filteredClientOptions =
    clientOptions?.filter(
      (item) => item.clientInternalNumberGos !== selectedClient?.clientInternalNumberGos
    ) || [];

  if (!clientOptions.length) return null;

  const withMultiClients = Boolean(clientOptions?.length > 1);

  const clientDocuments = selectedClient?.attachments || [];

  const disableChangeClient = withMultiClients && !chatSessionId;

  return (
    <>
      <Container $withMultiClients={withMultiClients}>
        <Menu open={open} onOpenChange={onOpenChange}>
          <MenuTrigger disableButtonEnhancement>
            {(props: MenuTriggerChildProps<"button">) => (
              <Button
                disabled={disableChangeClient}
                {...props}
                data-help="nav-client-switcher"
                appearance="transparent"
                style={{
                  height: "100%",
                  width: "100%",
                  paddingRight: "5px",
                  // background: theme.palette.purpleLight,
                  background: theme.palette.purpleLight,
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
                icon={<ChevronUpDownFilled />}
                iconPosition="after"
              >
                <Tooltip content={selectedClient?.clientName || ""} relationship="label">
                  <Text
                    as="span"
                    truncate
                    wrap={false}
                    weight="semibold"
                    size={400}

                    // onClick={triggerProps.onClick}
                  >
                    <ClientBgImg
                      name={selectedClient?.clientName}
                      shape="square"
                      style={{
                        marginRight: "5px",
                      }}
                      image={{
                        src: selectedClient?.backgroundPicturePath,
                      }}
                    />
                    {selectedClient?.clientName}
                  </Text>
                </Tooltip>
              </Button>
            )}
          </MenuTrigger>

          {withMultiClients ? (
            <MenuPopover>
              <MenuList>
                {filteredClientOptions?.map((item) => (
                  <MenuItem
                    key={item.clientInternalNumberGos}
                    onClick={() => {
                      setSelectedClient(item);
                      navigate(ROUTES.HOME);
                      setSelectedProfileTab(EProfileTab.info);
                      if (togglePanel) {
                        togglePanel();
                      }
                    }}
                  >
                    <Flex>
                      <ClientBgImg
                        name={item?.clientName}
                        shape="square"
                        image={{
                          src: item?.backgroundPicturePath,
                        }}
                      />
                      {item.clientName}
                    </Flex>
                  </MenuItem>
                ))}
              </MenuList>
            </MenuPopover>
          ) : (
            <></>
          )}
        </Menu>
      </Container>
      {showDocuments && clientDocuments?.length && (
        <DocumentsDialog
          title={t("hbh.companyDocuments.label")}
          open={showDocuments}
          setOpen={setDocumentOpenState}
          documents={clientDocuments}
        />
      )}
    </>
  );
}

const Container = styled.div<{
  $withMultiClients?: boolean;
}>`
  display: flex;
  width: 100%;

  .fui-Button__icon {
    color: ${({ theme }) => theme.palette.black};
    font-size: 14px;
    font-weight: 600;
    display: ${({ $withMultiClients }) => ($withMultiClients ? "flex" : "none !important")};
  }

  display: flex;
  align-items: center;
  gap: 5px;
  overflow: hidden;
  width: 100%;
  display: block;
  color: ${({ theme }) => theme.palette.black};

  button:disabled,
  button:focus,
  button:active {
    color: ${({ theme }) => theme.palette.themePrimary} !important;
    cursor: default !important;
  }
`;

const ClientBgImg = styled(Avatar)`
  width: 30px;
  height: 30px;
`;
